import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, VStack, Spinner, Heading, Text, Button, HStack } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WorkshopHero from '../components/workshop/WorkshopHero';
import WorkshopSchedule from '../components/workshop/WorkshopSchedule';
import WorkshopAbout from '../components/workshop/WorkshopAbout';
import WorkshopInstructor from '../components/workshop/WorkshopInstructor';
import WorkshopOutcomes from '../components/workshop/WorkshopOutcomes';
import WorkshopTestimonials from '../components/workshop/WorkshopTestimonials';
import WorkshopFAQ from '../components/workshop/WorkshopFAQ';
import RegistrationForm from '../components/workshop/RegistrationForm';
import TrustBadges from '../components/workshop/TrustBadges';
import { useWorkshopData } from '../hooks/useWorkshopData';
import { useAnalytics } from '../hooks/useAnalytics';
import trackingService from '../services/trackingService';

/**
 * Workshop Registration Landing Page
 * Dynamic page for single workshop signup at /register/:workshopId
 */
const WorkshopRegister = () => {
  const { workshopId: rawWorkshopId } = useParams();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const registrationRef = useRef(null);

  // Parse URL format: "workshopId-influencerId-flags"
  const parts = rawWorkshopId?.split('-') || [];
  const workshopId = parts[0] || '1';
  const influencerId = parts[1] || '';
  const flags = parts[2] || '';

  // Track influencer/affiliate code from URL path
  React.useEffect(() => {
    if (influencerId) {
      trackingService.setInfluencer(influencerId);
      console.log('✅ Influencer code set from URL path:', influencerId);
    }
  }, [influencerId]);

  // Fetch workshop data
  const { workshop, location, loading, error } = useWorkshopData(workshopId);

  // Track page view
  React.useEffect(() => {
    if (workshop) {
      trackEvent('landing_page_view', {
        workshopId,
        influencerId,
        flags,
        workshopTitle: workshop.title,
      });
    }
  }, [workshop, workshopId, influencerId, flags, trackEvent]);

  const handleScrollToForm = () => {
    registrationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Loading state
  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="purple.600" />
          <Text color="gray.600">Loading workshop details...</Text>
        </VStack>
      </Box>
    );
  }

  // Error state
  if (error || !workshop) {
    return (
      <Box minH="100vh">
        <Navbar />
        <Container maxW="container.md" py={20}>
          <VStack spacing={8} textAlign="center">
            <Heading color="red.600">Workshop Not Found</Heading>
            <Text color="gray.600" fontSize="lg">
              {error || 'The workshop you are looking for does not exist.'}
            </Text>
            <HStack spacing={4}>
              <Button colorScheme="purple" onClick={() => navigate('/workshops')}>
                Browse All Workshops
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </HStack>
          </VStack>
        </Container>
        <Footer />
      </Box>
    );
  }

  // Success render
  const pageTitle = `${workshop.title} | Free Workshop | Ojasa Mirai`;
  const pageDescription = workshop.description || 'Register for this free workshop and learn from industry experts.';

  return (
    <Box>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <WorkshopHero workshop={workshop} onScroll={handleScrollToForm} />

      {/* Trust Badges */}
      {workshop.trustBadges && (
        <Box bg="white" py={6} borderBottom="1px solid" borderColor="gray.200">
          <Container maxW="container.lg">
            <TrustBadges badges={workshop.trustBadges} />
          </Container>
        </Box>
      )}

      {/* Schedule Section */}
      <WorkshopSchedule workshop={workshop} location={location} />

      {/* About Section */}
      <WorkshopAbout workshop={workshop} />

      {/* Instructor Section */}
      <WorkshopInstructor workshop={workshop} />

      {/* Outcomes Section */}
      <WorkshopOutcomes workshop={workshop} />

      {/* Testimonials Section */}
      <WorkshopTestimonials workshop={workshop} />

      {/* FAQ Section */}
      <WorkshopFAQ workshop={workshop} />

      {/* Registration Form */}
      <Box ref={registrationRef}>
        <RegistrationForm
          workshopId={workshopId}
          workshop={workshop}
          location={location}
          influencerId={influencerId}
          flags={flags}
        />
      </Box>

      <Footer />
    </Box>
  );
};

export default WorkshopRegister;
